import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Switch,
  TimePicker,
  message,
  Popconfirm,
  Alert
} from 'antd';
import { PlusOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { notificationTaskService, NotificationTask } from '../../services/notificationTaskService';

const { Option } = Select;
const { TextArea } = Input;

type CronGenerateMode = 'every_second' | 'every_minute' | 'daily' | 'monthly';

type CronGeneratorState = {
  mode: CronGenerateMode;
  time: dayjs.Dayjs;
  monthDay: number;
  secondInterval: number;
  minuteInterval: number;
};

const DEFAULT_CRON_TIME = dayjs('00:00:00', 'HH:mm:ss');

const cronWeekMap: Record<string, string> = {
  SUN: '0',
  MON: '1',
  TUE: '2',
  WED: '3',
  THU: '4',
  FRI: '5',
  SAT: '6'
};

const normalizeCronWeekField = (field: string) =>
  field.replace(/SUN|MON|TUE|WED|THU|FRI|SAT/g, (value) => cronWeekMap[value] || value);

const normalizeCronExpressionForBackend = (value?: string) => {
  const text = (value || '').trim();
  if (!text) return text;

  let fields = text.split(/\s+/);
  // qnn-react-cron outputs Quartz-style 7 fields:
  // second minute hour day month week year. The backend supports 6 fields
  // without year, so drop the year field before saving.
  if (fields.length === 7) {
    fields = fields.slice(0, 6);
  }

  if (fields.length === 6) {
    fields[5] = normalizeCronWeekField(fields[5]);
  } else if (fields.length === 5) {
    fields[4] = normalizeCronWeekField(fields[4]);
  }

  return fields.join(' ');
};

const buildCronExpression = (
  mode: CronGenerateMode,
  timeValue = DEFAULT_CRON_TIME,
  monthDay = 1,
  secondInterval = 1,
  minuteInterval = 1
) => {
  const hour = timeValue.hour();
  const minute = timeValue.minute();
  const second = timeValue.second();
  const normalizedSecondInterval = Math.min(Math.max(Math.floor(secondInterval || 1), 1), 59);
  const normalizedMinuteInterval = Math.min(Math.max(Math.floor(minuteInterval || 1), 1), 59);

  switch (mode) {
    case 'every_second':
      return normalizedSecondInterval === 1 ? '* * * * * ?' : `0/${normalizedSecondInterval} * * * * ?`;
    case 'every_minute':
      return normalizedMinuteInterval === 1 ? '0 * * * * ?' : `0 0/${normalizedMinuteInterval} * * * ?`;
    case 'daily':
      return `${second} ${minute} ${hour} * * ?`;
    case 'monthly':
      return `${second} ${minute} ${hour} ${monthDay} * ?`;
    default:
      return '0 * * * * ?';
  }
};

const inferCronGeneratorState = (value?: string): CronGeneratorState => {
  const expression = normalizeCronExpressionForBackend(value);
  const fields = expression.split(/\s+/);
  const fallback: CronGeneratorState = {
    mode: 'every_minute',
    time: DEFAULT_CRON_TIME,
    monthDay: 1,
    secondInterval: 1,
    minuteInterval: 1
  };

  if (fields.length !== 6) {
    return fallback;
  }

  const [second, minute, hour, day, month, week] = fields;
  const weekAny = week === '?' || week === '*';
  if (!weekAny || month !== '*') {
    return fallback;
  }

  const parseStepInterval = (field: string) => {
    if (field === '*') return 1;
    const matched = field.match(/^(?:\*|0)\/([1-9]\d*)$/);
    if (!matched) return 0;
    const interval = Number(matched[1]);
    return Number.isInteger(interval) && interval >= 1 && interval <= 59 ? interval : 0;
  };

  const secondInterval = parseStepInterval(second);
  if (secondInterval > 0 && minute === '*' && hour === '*' && day === '*') {
    return { ...fallback, mode: 'every_second' as CronGenerateMode, secondInterval };
  }
  const minuteInterval = parseStepInterval(minute);
  if (second === '0' && minuteInterval > 0 && hour === '*' && day === '*') {
    return { ...fallback, mode: 'every_minute' as CronGenerateMode, minuteInterval };
  }

  const parsedSecond = Number(second);
  const parsedMinute = Number(minute);
  const parsedHour = Number(hour);
  if (
    Number.isInteger(parsedSecond) &&
    Number.isInteger(parsedMinute) &&
    Number.isInteger(parsedHour) &&
    parsedSecond >= 0 &&
    parsedSecond <= 59 &&
    parsedMinute >= 0 &&
    parsedMinute <= 59 &&
    parsedHour >= 0 &&
    parsedHour <= 23
  ) {
    const parsedTime = dayjs()
      .hour(parsedHour)
      .minute(parsedMinute)
      .second(parsedSecond);
    if (day === '*') {
      return { ...fallback, mode: 'daily' as CronGenerateMode, time: parsedTime };
    }
    const parsedDay = Number(day);
    if (Number.isInteger(parsedDay) && parsedDay >= 1 && parsedDay <= 31) {
      return {
        ...fallback,
        mode: 'monthly' as CronGenerateMode,
        time: parsedTime,
        monthDay: parsedDay
      };
    }
  }

  return fallback;
};

const ScheduledTaskConfig: React.FC = () => {
  const [tasks, setTasks] = useState<NotificationTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<NotificationTask | null>(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cronValue, setCronValue] = useState('');
  const [cronGenerateMode, setCronGenerateMode] = useState<CronGenerateMode>('every_minute');
  const [cronGenerateTime, setCronGenerateTime] = useState(DEFAULT_CRON_TIME);
  const [cronGenerateMonthDay, setCronGenerateMonthDay] = useState(1);
  const [cronGenerateSecondInterval, setCronGenerateSecondInterval] = useState(1);
  const [cronGenerateMinuteInterval, setCronGenerateMinuteInterval] = useState(1);

  const syncCronExpression = (
    mode = cronGenerateMode,
    timeValue = cronGenerateTime,
    monthDay = cronGenerateMonthDay,
    secondInterval = cronGenerateSecondInterval,
    minuteInterval = cronGenerateMinuteInterval
  ) => {
    const expression = buildCronExpression(mode, timeValue, monthDay, secondInterval, minuteInterval);
    setCronValue(expression);
    form.setFieldValue('cronExpression', expression);
  };

  const resetCronGenerator = () => {
    setCronGenerateMode('every_minute');
    setCronGenerateTime(DEFAULT_CRON_TIME);
    setCronGenerateMonthDay(1);
    setCronGenerateSecondInterval(1);
    setCronGenerateMinuteInterval(1);
  };

  // 加载定时任务
  const loadTasks = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await notificationTaskService.list({ page, pageSize });
      let list: NotificationTask[] = [];
      let total = 0;
      
      if (response?.data?.list) {
        list = response.data.list;
        total = response.data.total || 0;
      } else if (response?.list) {
        list = response.list;
        total = response.total || 0;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
        total = list.length;
      }
      
      setTasks(list);
      setPagination({ page, pageSize, total });
    } catch (error) {
      console.error('加载定时任务失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const columns = [
    { title: '任务名称', dataIndex: 'name', key: 'name' },
    { 
      title: '接口地址', 
      dataIndex: 'apiUrl', 
      key: 'apiUrl',
      ellipsis: true,
      render: (url: string, record: any) => url || record.type || '-'
    },
    { 
      title: '请求方式', 
      dataIndex: 'httpMethod', 
      key: 'httpMethod',
      width: 90,
      render: (method: string) => {
        const methodMap: Record<string, string> = {
          'GET': 'green',
          'POST': 'blue',
          'PUT': 'orange',
          'DELETE': 'red'
        };
        return <Tag color={methodMap[method] || 'default'}>{method || 'GET'}</Tag>;
      }
    },
    { 
      title: '执行周期', 
      dataIndex: 'cronExpression', 
      key: 'cronExpression',
      width: 150,
      render: (cron: string) => {
        if (!cron) return '-';
        const cronMap: Record<string, string> = {
          '* * * * *': '每分钟',
          '0 * * * *': '每小时',
          '0 0 * * *': '每天零点',
          '0 8 * * *': '每天8点',
          '0 9 * * *': '每天9点',
          '0 0 1 * *': '每月一号',
          '0 0 * * 0': '每周日',
          '0 0 * * 1': '每周一'
        };
        return cronMap[cron] || cron;
      }
    },
    { 
      title: '状态', 
      dataIndex: 'isEnabled', 
      key: 'isEnabled',
      width: 100,
      render: (enabled: boolean, record: NotificationTask) => (
        <Switch 
          checked={enabled} 
          onChange={() => handleToggleTask(record.id)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      )
    },
    { title: '执行次数', dataIndex: 'runCount', key: 'runCount', width: 90 },
    { 
      title: '最后执行', 
      dataIndex: 'lastRunTime', 
      key: 'lastRunTime',
      width: 160,
      render: (time: string) => time || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: unknown, record: NotificationTask) => (
        <Space>
          <Popconfirm title="确定立即执行此任务？" onConfirm={() => handleExecuteTask(record.id)}>
            <Button type="link" size="small" style={{ color: '#52c41a' }}>立即执行</Button>
          </Popconfirm>
          <Button type="link" size="small" onClick={() => handleEditTask(record)}>编辑</Button>
          <Popconfirm title="确定删除此任务？" onConfirm={() => handleDeleteTask(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleEditTask = (record: NotificationTask) => {
    setEditingTask(record);
    const cronExpression = normalizeCronExpressionForBackend(record.cronExpression);
    const generatorState = inferCronGeneratorState(cronExpression);
    setCronValue(cronExpression || '');
    setCronGenerateMode(generatorState.mode);
    setCronGenerateTime(generatorState.time);
    setCronGenerateMonthDay(generatorState.monthDay);
    setCronGenerateSecondInterval(generatorState.secondInterval);
    setCronGenerateMinuteInterval(generatorState.minuteInterval);
    form.setFieldsValue({
      name: record.name,
      apiUrl: record.apiUrl || '',
      httpMethod: record.httpMethod || 'GET',
      cronExpression,
      requestParams: record.requestParams || record.notificationTemplate,
      isEnabled: record.isEnabled
    });
    setIsModalVisible(true);
  };

  const handleDeleteTask = async (id: string) => {
    try {
      await notificationTaskService.delete(id);
      message.success('删除成功');
      loadTasks(pagination.page, pagination.pageSize);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggleTask = async (id: string) => {
    try {
      await notificationTaskService.toggleEnabled(id);
      message.success('状态切换成功');
      loadTasks(pagination.page, pagination.pageSize);
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleExecuteTask = async (id: string) => {
    try {
      const response = await notificationTaskService.execute(id);
      const result = response?.data || response;
      message.success(result?.message || '任务执行成功');
      loadTasks(pagination.page, pagination.pageSize);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '执行失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);
      const cronExpression = normalizeCronExpressionForBackend(values.cronExpression);
      const data = {
        name: values.name,
        type: 'http_request',
        apiUrl: values.apiUrl,
        httpMethod: values.httpMethod,
        cronExpression,
        notificationTemplate: values.requestParams,
        requestParams: values.requestParams,
        isEnabled: values.isEnabled
      };
      
      if (editingTask) {
        await notificationTaskService.update(editingTask.id, data);
        message.success('更新成功');
      } else {
        await notificationTaskService.create(data);
        message.success('创建成功');
      }
      setIsModalVisible(false);
      setEditingTask(null);
      form.resetFields();
      loadTasks(pagination.page, pagination.pageSize);
    } catch (error) {
      message.error('操作失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Card title={<><ClockCircleOutlined /> 定时任务</>}>
      <Alert
        title="定时任务说明"
        description="管理系统中的定时任务，包括数据备份、报表生成、通知提醒等自动化任务。可以配置执行周期、执行时间，并支持手动立即执行。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => { 
            setEditingTask(null); 
            form.resetFields(); 
            resetCronGenerator();
            const expression = buildCronExpression('every_minute', DEFAULT_CRON_TIME, 1, 1, 1);
            setCronValue(expression);
            form.setFieldValue('cronExpression', expression);
            setIsModalVisible(true); 
          }}
        >
          添加定时任务
        </Button>
      </div>
      <Table
        dataSource={tasks}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => loadTasks(page, pageSize)
        }}
      />

      <Modal
        title={editingTask ? '编辑定时任务' : '添加定时任务'}
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); setEditingTask(null); form.resetFields(); }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
            <Input placeholder="请输入任务名称" />
          </Form.Item>
          <Form.Item name="apiUrl" label="接口地址" rules={[{ required: true, message: '请输入接口地址' }]}>
            <Input placeholder="请输入接口地址，如：/api/v1/admin/notification-tasks/execute" />
          </Form.Item>
          <Form.Item name="httpMethod" label="请求方式" initialValue="GET">
            <Select placeholder="请选择请求方式">
              <Option value="GET">GET</Option>
              <Option value="POST">POST</Option>
              <Option value="PUT">PUT</Option>
              <Option value="DELETE">DELETE</Option>
            </Select>
          </Form.Item>
          <Form.Item name="cronExpression" label="Cron表达式">
            <Input placeholder="请输入Cron表达式（高级配置）" value={cronValue} onChange={(e) => {
              setCronValue(e.target.value);
              form.setFieldValue('cronExpression', e.target.value);
            }} />
          </Form.Item>
          <Form.Item label="Cron生成器">
            <Space orientation="vertical" style={{ width: '100%' }} size={12}>
              <Select
                value={cronGenerateMode}
                style={{ width: '100%' }}
                onChange={(mode: CronGenerateMode) => {
                  setCronGenerateMode(mode);
                  syncCronExpression(
                    mode,
                    cronGenerateTime,
                    cronGenerateMonthDay,
                    cronGenerateSecondInterval,
                    cronGenerateMinuteInterval
                  );
                }}
              >
                <Option value="every_second">按秒间隔执行</Option>
                <Option value="every_minute">按分钟间隔执行</Option>
                <Option value="daily">每天固定时间执行</Option>
                <Option value="monthly">每月固定日期时间执行</Option>
              </Select>
              {cronGenerateMode === 'every_second' && (
                <InputNumber
                  min={1}
                  max={59}
                  value={cronGenerateSecondInterval}
                  addonBefore="每"
                  addonAfter="秒执行一次"
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    const nextInterval = Number(value || 1);
                    setCronGenerateSecondInterval(nextInterval);
                    syncCronExpression(cronGenerateMode, cronGenerateTime, cronGenerateMonthDay, nextInterval, cronGenerateMinuteInterval);
                  }}
                />
              )}
              {cronGenerateMode === 'every_minute' && (
                <InputNumber
                  min={1}
                  max={59}
                  value={cronGenerateMinuteInterval}
                  addonBefore="每"
                  addonAfter="分钟执行一次"
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    const nextInterval = Number(value || 1);
                    setCronGenerateMinuteInterval(nextInterval);
                    syncCronExpression(cronGenerateMode, cronGenerateTime, cronGenerateMonthDay, cronGenerateSecondInterval, nextInterval);
                  }}
                />
              )}
              {(cronGenerateMode === 'daily' || cronGenerateMode === 'monthly') && (
                <TimePicker
                  value={cronGenerateTime}
                  format="HH:mm:ss"
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    const nextTime = value || DEFAULT_CRON_TIME;
                    setCronGenerateTime(nextTime);
                    syncCronExpression(cronGenerateMode, nextTime, cronGenerateMonthDay);
                  }}
                />
              )}
              {cronGenerateMode === 'monthly' && (
                <InputNumber
                  min={1}
                  max={31}
                  value={cronGenerateMonthDay}
                  addonBefore="每月"
                  addonAfter="日执行"
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    const nextDay = Number(value || 1);
                    setCronGenerateMonthDay(nextDay);
                    syncCronExpression(cronGenerateMode, cronGenerateTime, nextDay);
                  }}
                />
              )}
              <Button onClick={() => syncCronExpression()}>生成Cron表达式</Button>
            </Space>
          </Form.Item>
          <Form.Item name="requestParams" label="请求参数">
            <TextArea rows={4} placeholder="请输入请求参数（JSON格式），如：{&quot;key&quot;: &quot;value&quot;}" />
          </Form.Item>
          <Form.Item name="isEnabled" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setIsModalVisible(false); setEditingTask(null); form.resetFields(); }}>取消</Button>
              <Button type="primary" htmlType="submit" loading={submitLoading}>确定</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ScheduledTaskConfig;
